import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  getProposalForRestaurant,
  Pack,
  ProjectPhase
} from '@/data/mocks/proposal';
import { restaurants, getRestaurantById } from '@/data/mocks/restaurants';
import { executiveSummary39V } from '@/data/mocks/strategicAnalysis';
import { useSelectedRestaurant } from '@/hooks/useSelectedRestaurant';
import {
  FileText,
  Check,
  Download,
  Calendar,
  Clock,
  Target,
  Award,
  Star,
  Package,
  Zap,
  ArrowRight,
  Search,
  Globe,
  Languages,
  ListChecks,
  TrendingUp,
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  Euro,
  Sparkles
} from 'lucide-react';

export const ProposalPage: React.FC = () => {
  const { selectedRestaurant, setSelectedRestaurant } = useSelectedRestaurant();
  const proposalRef = useRef<HTMLDivElement>(null);

  const currentRestaurant = getRestaurantById(selectedRestaurant);
  const proposal = getProposalForRestaurant(selectedRestaurant);

  // Fonction d'export PDF
  const exportToPDF = async () => {
    if (!proposalRef.current) return;

    try {
      const canvas = await html2canvas(proposalRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`proposition-stratos-${currentRestaurant?.slug}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
    }
  };

  // Composant Timeline
  const Timeline: React.FC<{ phases: ProjectPhase[] }> = ({ phases }) => {
    return (
      <div className="space-y-8">
        {phases.map((phase, index) => (
          <div key={phase.id} className="relative">
            {/* Ligne verticale */}
            {index < phases.length - 1 && (
              <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200" />
            )}

            <div className="flex gap-8">
              {/* Indicateur de phase */}
              <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600">
                {index + 1}
              </div>

              {/* Contenu de la phase */}
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">{phase.name}</h3>
                <div className="text-sm text-gray-600 mb-4">
                  Durée : {phase.duration} jours
                </div>

                {/* Tâches */}
                <div className="space-y-3">
                  {phase.tasks.map((task, taskIndex) => (
                    <div key={taskIndex} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-400" />
                      <span>{task.name}</span>
                      <span className="text-sm text-gray-500">
                        ({task.duration}j)
                      </span>
                    </div>
                  ))}
                </div>

                {/* Livrables */}
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Livrables :</h4>
                  <div className="flex flex-wrap gap-2">
                    {phase.deliverables.map((deliverable, i) => (
                      <Badge key={i} variant="outline">
                        {deliverable}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const summary = selectedRestaurant === '39v' ? executiveSummary39V : null;

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* En-tête avec sélecteur d'établissement */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Sparkles className="w-7 h-7 text-stratos-gold" />
            Proposition Commerciale STRATOS
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Solutions SEO & GEO sur-mesure pour votre restaurant</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {Object.values(restaurants).map((restaurant) => (
            <Button
              key={restaurant.id}
              variant={selectedRestaurant === restaurant.id ? "default" : "outline"}
              onClick={() => setSelectedRestaurant(restaurant.id)}
              className="flex items-center gap-2 justify-center"
            >
              <Package className="w-4 h-4" />
              {restaurant.name}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Zone d'export */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">Proposition pour {currentRestaurant?.name}</h3>
                  <p className="text-sm text-gray-600">
                    Valide jusqu'au {new Date(proposal.validUntil).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
              <Button onClick={exportToPDF} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Exporter PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Contexte et Enjeux */}
      {summary && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="border-2 border-stratos-gold bg-gradient-to-br from-gray-50 to-white">
            <CardHeader>
              <CardTitle className="text-xl sm:text-2xl flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-stratos-gold" />
                Contexte & Enjeux - {currentRestaurant?.name}
              </CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Diagnostic de votre situation actuelle et opportunités identifiées
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs font-medium text-red-700 mb-2">Perte Estimée</p>
                  <p className="text-2xl font-bold text-red-600">{summary.estimatedLostRevenue}</p>
                  <p className="text-xs text-gray-600 mt-1">Chiffre d'affaires perdu par an</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs font-medium text-green-700 mb-2">Potentiel Quick Wins</p>
                  <p className="text-2xl font-bold text-green-600">{summary.quickWinsPotential}</p>
                  <p className="text-xs text-gray-600 mt-1">Croissance en 3 mois</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs font-medium text-blue-700 mb-2">ROI Estimé</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.estimatedROI}</p>
                  <p className="text-xs text-gray-600 mt-1">Retour sur investissement</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {summary.keyFindings.slice(0, 4).map((finding, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-gray-800">{finding}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* AUDIT COMPLET - Prérequis */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="border-4 border-stratos-gold shadow-2xl bg-gradient-to-br from-white to-stratos-gold/5">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-stratos-gold rounded-lg">
                    <Search className="w-6 h-6 text-gray-900" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl sm:text-3xl">Audit Complet SEO & GEO</CardTitle>
                    <Badge className="mt-2 bg-stratos-gold text-gray-900 hover:bg-stratos-gold-dark">
                      Prérequis Obligatoire
                    </Badge>
                  </div>
                </div>
                <CardDescription className="text-base">
                  Diagnostic approfondi de votre présence digitale et plan d'action stratégique personnalisé
                </CardDescription>
              </div>
              <div className="text-center sm:text-right bg-white p-4 sm:p-6 rounded-xl border-2 border-stratos-gold">
                <p className="text-sm text-gray-600 mb-1">Investissement</p>
                <p className="text-4xl sm:text-5xl font-bold text-stratos-gold">4 000€</p>
                <p className="text-sm text-gray-600 mt-1">HT - One-time</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Ce qui est inclus */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-gray-900">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Audits Techniques Complets
                </h4>
                <div className="space-y-2 pl-7">
                  <div className="flex items-start gap-2">
                    <Search className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Audit SEO technique (39 points de contrôle)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Globe className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Audit GEO local & Google My Business</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Languages className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Audit internationalisation (EN/ES/PT)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <BarChart3 className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Analyse concurrentielle approfondie</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-gray-900">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Livrables & Recommandations
                </h4>
                <div className="space-y-2 pl-7">
                  <div className="flex items-start gap-2">
                    <TrendingUp className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Estimation d'impact de chaque action (CA)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ListChecks className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Priorisation ICE Score (Impact/Confiance/Effort)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Target className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Plan d'action détaillé 90 jours</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-stratos-gold mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Rapport complet + présentation stratégique</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Demandes spécifiques */}
            <div className="bg-stratos-gold/10 p-4 rounded-lg border border-stratos-gold/30">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-stratos-gold" />
                Inclus : Analyse de Vos Demandes Spécifiques
              </h4>
              <p className="text-sm text-gray-700">
                L'audit intègre vos objectifs business spécifiques : privatisation, événementiel, 
                clientèle internationale, segments de marché ciblés, concurrents locaux, etc.
              </p>
            </div>

            {/* CTA Audit */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                size="lg"
                className="flex-1 bg-gradient-to-r from-stratos-gold to-stratos-gold-dark text-gray-900 hover:shadow-lg hover:shadow-stratos-gold/30"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Commander l'Audit Complet
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={exportToPDF}
                className="border-stratos-gold text-stratos-gold-dark hover:bg-stratos-gold/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger la Proposition
              </Button>
            </div>

            {/* Délai */}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>Livraison de l'audit : <strong className="text-gray-900">10-15 jours ouvrés</strong></span>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Séparateur avec message */}
      <div className="relative py-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t-2 border-gray-300"></div>
        </div>
        <div className="relative flex justify-center">
          <div className="px-6 bg-gray-50">
            <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-full border-2 border-stratos-gold shadow-lg">
              <ArrowRight className="w-5 h-5 text-stratos-gold" />
              <span className="font-semibold text-gray-900">Puis, choix de votre Pack d'Accompagnement</span>
              <ArrowRight className="w-5 h-5 text-stratos-gold" />
            </div>
          </div>
        </div>
      </div>

      {/* Contenu de la proposition */}
      <div ref={proposalRef} className="space-y-8">
        {/* Packs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Packs d'Accompagnement Mensuel
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Disponibles uniquement après réalisation de l'Audit Complet
            </p>
            <Badge variant="outline" className="mt-3">
              Engagement minimum 6 mois
            </Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {proposal.packs.map((pack, index) => (
            <Card
              key={pack.id}
              className={`relative transition-all hover:shadow-xl ${
                pack.recommended 
                  ? 'border-4 border-stratos-gold shadow-2xl scale-105 mt-6' 
                  : 'border-2 border-gray-200 hover:border-stratos-gold/50'
              }`}
            >
              {pack.recommended && (
                <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 z-10">
                  <Badge className="bg-gradient-to-r from-stratos-gold to-stratos-gold-dark text-gray-900 px-4 py-1.5 text-sm font-bold shadow-lg">
                    ⭐ RECOMMANDÉ
                  </Badge>
                </div>
              )}
              <CardHeader className={pack.recommended ? 'bg-gradient-to-br from-stratos-gold/10 to-white' : ''}>
                <div className="text-center mb-4">
                  <CardTitle className="text-xl sm:text-2xl mb-3">{pack.name}</CardTitle>
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-4xl sm:text-5xl font-bold text-gray-900">{pack.price}€</span>
                    <span className="text-gray-600">HT/mois</span>
                  </div>
                  <p className="text-xs text-gray-500">Engagement {pack.commitment} mois minimum</p>
                </div>
                <CardDescription className="text-center text-sm sm:text-base">{pack.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Features */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-gray-700 mb-3">Inclus dans ce pack :</h4>
                  {pack.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <CheckCircle2 className="w-5 h-5 text-stratos-gold flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-800">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Highlights */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2 justify-center">
                    {pack.highlights.map((highlight, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className="bg-stratos-gold/20 text-stratos-gold-dark border-stratos-gold/30"
                      >
                        {highlight}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Estimation ROI */}
                {pack.recommended && summary && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="text-xs font-medium text-green-700 text-center mb-1">ROI Estimé</p>
                    <p className="text-center text-lg font-bold text-green-600">{summary.estimatedROI}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          </div>
        </motion.div>

        {/* Planning */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Planning Projet
              </CardTitle>
              <CardDescription>
                Déroulement sur 3 mois
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Timeline phases={proposal.projectPhases} />
            </CardContent>
          </Card>
        </motion.div>

      </div>
    </div>
  );
};
